import { useEffect, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { COMMON_LIBRARY_CONSTANTS, getItemFromLocalStorage, saveItemWithExpiryToLocalStorage } from '@netfront/common-library';
import sortBy from 'lodash.sortby';
import uniqBy from 'lodash.uniqby';

import { IUseGetUserFlowsOnCompletedResponse, useGetUserFlows } from '../useGetUserFlows';
import {
  getToolBoxData,
  getToolBoxItems,
  IUseToolBoxOnCompletedResponse,
  TOOLBOX_USER_FLOW_IDS_STORAGE_EXPIRY_OPTIONS,
  useToolBox,
} from '../useToolBox';

import { TOOLBOX_MEDIA_TYPE_CONTENT_SNIPPET_TYPE_MAP } from './useToolBoxMedia.constants';
import { IUseToolBoxMedia, IUseToolBoxMediaOptions } from './useToolBoxMedia.interfaces';
import { ToolBoxMediaType } from './useToolBoxMedia.types';

import { IDBContentPage, IToolBoxData, IToolBoxMediaData, IToolBoxMediaContentSnippet } from '../../interfaces';

const {
  MESSAGES: {
    ERROR: { NOT_FOUND },
  },
} = COMMON_LIBRARY_CONSTANTS;

const useToolBoxMedia = ({
  fetchPolicy,
  mediaType,
  product,
  projectId,
  onCompleted,
  onError,
  token,
  userFlowIdsStorageExpiryTimeOptions,
}: IUseToolBoxMediaOptions): IUseToolBoxMedia => {
  const [selectedMediaType, setSelectedMediaType] = useState<ToolBoxMediaType>(mediaType);
  const [userFlowIdsFromLocalStorage, setUserFlowIdsFromLocalStorage] = useState<string | undefined>(
    getItemFromLocalStorage('userFlowIds')
  );

  const handleGetContentSnippetsForToolBoxCompleted = (data?: IUseToolBoxOnCompletedResponse) => {
    if (!data) {
      return;
    }

    if (!onCompleted) {
      return;
    }

    const { contentSnippets: returnedContentSnippets } = data;

    const contentSnippetTypes = TOOLBOX_MEDIA_TYPE_CONTENT_SNIPPET_TYPE_MAP[selectedMediaType];
    const filteredContentSnippets = returnedContentSnippets.filter(({ __typename }) => {
      return contentSnippetTypes.includes(__typename);
    });

    const toolboxData = filteredContentSnippets.reduce(
      (accumulator, currentContentSnippet) => {
        const { contentPages, contentSnippets, groupingContentGroups, userFlows, userFlowSteps } = accumulator;
        const { audio, embed, contentPage, document, id: contentSnippetId, __typename: typeName, video } = currentContentSnippet;

        const toolboxItems = getToolBoxItems(contentPage as IDBContentPage, typeName);

        const currentToolboxData: IToolBoxData = {
          contentPages,
          groupingContentGroups,
          userFlows,
          userFlowSteps,
        };

        const { toolboxContentPage, toolboxGroupingContentGroup, toolboxUserFlow, toolboxUserFlowStep } = toolboxItems;

        const {
          contentPages: combinedContentPages,
          groupingContentGroups: combinedGroupingContentGroups,
          userFlows: combinedUserFlows,
          userFlowSteps: combinedUserFlowSteps,
        } = getToolBoxData(currentToolboxData, toolboxItems);

        const toolboxContentSnippet: IToolBoxMediaContentSnippet = {
          audio,
          embed,
          contentPageId: toolboxContentPage.id,
          document,
          groupingContentGroupId: toolboxGroupingContentGroup ? toolboxGroupingContentGroup.id : undefined,
          id: contentSnippetId,
          typeName,
          userFlowId: toolboxUserFlow.id,
          userFlowStepId: toolboxUserFlowStep.id,
          video,
        };

        const combinedContentSnippets = sortBy(
          uniqBy([...contentSnippets, toolboxContentSnippet], ({ id }) => id),
          'sort'
        );

        return {
          ...accumulator,
          contentPages: combinedContentPages,
          contentSnippets: combinedContentSnippets,
          groupingContentGroups: combinedGroupingContentGroups,
          userFlows: combinedUserFlows,
          userFlowSteps: combinedUserFlowSteps,
        };
      },
      {
        contentPages: [],
        contentSnippets: [],
        groupingContentGroups: [],
        userFlows: [],
        userFlowSteps: [],
      } as IToolBoxMediaData
    );

    onCompleted({
      toolboxData,
    });
  };

  const handleGetContentSnippetsForToolBoxError = (error: ApolloError) => {
    if (!onError) {
      return;
    }

    onError(error);
  };

  const handleGetUserFlowsCompleted = (data?: IUseGetUserFlowsOnCompletedResponse) => {
    if (!data) {
      return;
    }

    const { userFlows } = data;

    if (!userFlows.length) {
      throw new Error(`No user flows found for this project id=${projectId}`);
    }

    const userFlowIds = userFlows.map(({ id }) => id).join(',');

    saveItemWithExpiryToLocalStorage(
      'userFlowIds',
      userFlowIds,
      userFlowIdsStorageExpiryTimeOptions ?? TOOLBOX_USER_FLOW_IDS_STORAGE_EXPIRY_OPTIONS
    );

    setUserFlowIdsFromLocalStorage(userFlowIds);
  };

  const handleGetUserFlowsError = (error: ApolloError) => {
    if (!onError) {
      return;
    }

    onError(error);
  };

  const { handleGetContentSnippetsForToolBox, isLoading: isGetContentSnippetsForToolBoxLoading = false } = useToolBox({
    fetchPolicy,
    onCompleted: handleGetContentSnippetsForToolBoxCompleted,
    onError: handleGetContentSnippetsForToolBoxError,
    product,
    token,
  });

  const { handleGetUserFlows, isLoading: isGetUserFlowsLoading = false } = useGetUserFlows({
    fetchPolicy: 'no-cache',
    onCompleted: handleGetUserFlowsCompleted,
    onError: handleGetUserFlowsError,
    product,
    token,
  });

  useEffect(() => {
    setSelectedMediaType(mediaType);
  }, [mediaType]);

  useEffect(() => {
    if (userFlowIdsFromLocalStorage) {
      return;
    }

    handleGetUserFlows({
      projectId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaType, userFlowIdsFromLocalStorage]);

  const handleGetToolBoxMedia = () => {
    if (!userFlowIdsFromLocalStorage) {
      throw new Error(`User flow ids ${NOT_FOUND}`);
    }

    const userFlowIds = userFlowIdsFromLocalStorage.split(',').map((userFlowId) => Number(userFlowId));

    if (selectedMediaType === 'audio') {
      handleGetContentSnippetsForToolBox({
        projectId,
        shouldIncludeApps: false,
        shouldIncludeAudios: true,
        shouldIncludeContentPage: true,
        shouldIncludeDocuments: false,
        shouldIncludeEmbeds: false,
        shouldIncludeForms: false,
        shouldIncludeUserFlowStep: true,
        shouldIncludeVideos: false,
        userFlowIds,
      });

      return;
    }

    if (selectedMediaType === 'documents') {
      handleGetContentSnippetsForToolBox({
        projectId,
        shouldIncludeApps: false,
        shouldIncludeAudios: false,
        shouldIncludeContentPage: true,
        shouldIncludeDocuments: true,
        shouldIncludeEmbeds: false,
        shouldIncludeForms: false,
        shouldIncludeUserFlowStep: true,
        shouldIncludeVideos: false,
        userFlowIds,
      });

      return;
    }

    handleGetContentSnippetsForToolBox({
      projectId,
      shouldIncludeApps: false,
      shouldIncludeAudios: false,
      shouldIncludeContentPage: true,
      shouldIncludeDocuments: false,
      shouldIncludeEmbeds: true,
      shouldIncludeForms: false,
      shouldIncludeUserFlowStep: true,
      shouldIncludeVideos: true,
      userFlowIds,
    });
  };

  return {
    handleGetToolBoxMedia,
    isLoading: isGetContentSnippetsForToolBoxLoading || isGetUserFlowsLoading,
    isReady: Boolean(userFlowIdsFromLocalStorage),
  };
};

export { useToolBoxMedia };
